import { useCallback, useState, useRef, useEffect } from 'react';
import Script from 'next/script';

const CrLocationField = ({ register, setValue, searchForm, className }) => {
  const [initLocationLoaded, setInitLocationLoaded] = useState(false);
  const [autoCompleteLoaded, setAutoCompleteLoaded] = useState(false);

  const submitOnPlaceChanged = useRef(false);

  const initCallback = useCallback(async () => {
    if (!initLocationLoaded) {
      await getLocationFromBrowser(setValue);
      setInitLocationLoaded(true);
    }
    if (!autoCompleteLoaded) {
      if (autocompleteInit(setValue, submitOnPlaceChanged, searchForm)) {
        setAutoCompleteLoaded(true);
      }
    }
  }, [
    setValue,
    searchForm,
    initLocationLoaded,
    setInitLocationLoaded,
    autoCompleteLoaded,
    setAutoCompleteLoaded
  ]);

  const googleScriptLoaded = useCallback(() => {
    const googleLoadedEvent = new CustomEvent('google.maps.loaded');
    document.dispatchEvent(googleLoadedEvent);
    initCallback();
  }, [initCallback]);

  useEffect(() => {
    initCallback();
  }, [initCallback]);

  const setSubmitOnPlaceChangeIfUserPressedEnter = useCallback(
    (e) => {
      // this same function is in CR6 - it's to prevent the form from
      // submitting *before* the autocomplete callback has a chance to
      // populate the latitude and longitude fields. Tried and failed to
      // do away with it ~EN
      if (e.keyCode === 13) {
        e.preventDefault();
        submitOnPlaceChanged.current = true;
      }
    },
    [submitOnPlaceChanged]
  );

  return (
    <>
      <Script
        onLoad={googleScriptLoaded}
        src={`//maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&libraries=places`}
      />

      <input
        {...register('location')}
        className={className}
        id="jobsearch_location"
        placeholder="Location"
        spellCheck={false}
        onKeyDown={setSubmitOnPlaceChangeIfUserPressedEnter}
        type="text"
        autoComplete="off"
      />
    </>
  );
};

const autocompleteInit = (setValue, submitOnPlaceChanged, searchForm) => {
  if (!window || typeof window === 'undefined') {
    return;
  }

  if (
    typeof google === 'undefined' ||
    !google.maps ||
    !google.maps.places ||
    !google.maps.places.Autocomplete
  ) {
    return;
  }

  const el = document.getElementById('jobsearch_location');
  const autocomplete = new google.maps.places.Autocomplete(el);
  const doSubmit = submitOnPlaceChanged; // this will be {current: true} if the user selected a location by hitting "enter"
  const form = searchForm;

  google.maps.event.addListener(autocomplete, 'place_changed', function () {
    const place = autocomplete.getPlace();

    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      setValue('latitude', lat);
      setValue('longitude', lng);
    } else {
      setValue('latitude', null);
      setValue('longitude', null);
    }

    if (doSubmit.current) {
      // this will be true if the user selected a location by hitting "enter"
      doSubmit.current = false;
      form.current.submit();
    }
  });

  return true;
};

const getLocationFromBrowser = async (setValue) => {
  if (!window || typeof window === 'undefined') {
    return;
  }

  const params = new URLSearchParams(location.search);
  if (params.get('location')) {
    // don't overwrite a location the user has already provided
    return;
  }

  const storageExists = 'sessionStorage' in window;

  if (storageExists) {
    const data = sessionStorage.getItem('currentLocation');
    if (data) {
      const parsed = JSON.parse(data);
      setValue('latitude', parsed.coords.latitude);
      setValue('longitude', parsed.coords.longitude);
      setValue('location', parsed.addr);
      return;
    }
  }

  if (!('geolocation' in navigator)) {
    return;
  }

  if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
    return;
  }

  navigator.geolocation.getCurrentPosition(async ({ coords }) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode(
      { location: { lat: coords.latitude, lng: coords.longitude } },
      function (data, status) {
        if (status === 'OK' && data[0]) {
          const result = {
            coords: {
              latitude: coords.latitude,
              longitude: coords.longitude
            },
            addr: data[0].formatted_address
          };
          setValue('latitude', result.coords.latitude);
          setValue('longitude', result.coords.longitude);
          setValue('location', result.addr);
          if (storageExists) {
            sessionStorage.setItem('currentLocation', JSON.stringify(result));
          }
        }
      },
      (err) => {
        console.log('error', err);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    );
  });
};

module.exports = CrLocationField;
